import { z, type ZodType } from 'zod';

import { jsonRequest, resolveApiFetch } from './shared';
import { assertOk, parseJson } from './response';
import { loadOfflineSnapshot, saveOfflineSnapshot } from '@/lib/offline/dataStore';
import { generateClientRequestId } from '@/lib/offline/clientRequestId';
import { OFFLINE_PUBLIC_TABLE_VERSION } from '@/lib/offline/constants';
import type { OfflineQueuedResult } from '@/lib/offline/types';
import type { MenuAvailabilityReason, MenuAvailabilityState, MenuAvailabilityWindow } from '@/types/menuAvailability';
import {
  parseQrOrderingNotice,
  type QrOrderingNotice,
} from '@/lib/ordering/qrOrderingNotice';

export type PublicTasteProfile = {
  tags?: string[];
  heat?: 'none' | 'mild' | 'medium' | 'hot' | 'extra_hot' | string;
  cuisines?: string[];
  tagScores?: Record<string, number>;
  cuisineAffinity?: Record<string, number>;
  updatedAt?: number;
  source?: 'stored' | 'derived' | 'fallback';
  // legacy aliases
  tag_scores?: Record<string, number>;
  cuisine_affinity?: Record<string, number>;
  updated_at?: number;
};

export interface PublicTableItemModifier {
  id: string;
  name: string;
  price_delta_cents: number;
  translations?: Record<string, { name?: string }>;
}

export interface PublicTableItem {
  id: string;
  category_id: string | null;
  name: string;
  description: string | null;
  price_cents: number;
  is_available: boolean;
  availability: MenuAvailabilityState;
  image_url: string | null;
  image_alt_text: string | null;
  image_spice_level: string | null;
  modifiers: PublicTableItemModifier[];
  margin_cents?: number | null;
  prep_minutes?: number | null;
  station?: string | null;
  is_new?: boolean | null;
  translations?: Record<string, { name?: string; description?: string; imageAltText?: string | null }>;
  metadata?: Record<string, unknown>;
  tags?: string[];
  taste_profile?: PublicTasteProfile | null;
  average_rating?: number | null;
  review_count?: number;
}

export interface PublicGuestProfile {
  external_id: string | null;
  name: string | null;
  loyalty_points: number;
  favorite_item_ids: string[];
  last_order_snapshot?: Record<string, unknown> | null;
  consent?: Record<string, unknown> | null;
  scheduled_preferences?: Record<string, unknown> | null;
  taste_profile?: PublicTasteProfile | null;
  taste_tags?: string[];
  taste_cuisines?: string[];
  taste_heat?: string | null;
  taste_tag_scores?: Record<string, number>;
  taste_cuisine_affinity?: Record<string, number>;
  taste_profile_updated_at?: number | null;
  reward?: {
    type: string;
    description?: string | null;
    token: string;
  } | null;
}

export interface PublicSchedulingWindow {
  id: string;
  label: string;
  start: number;
  end: number;
}

export interface PublicTableScheduling {
  timezone: string;
  windows: PublicSchedulingWindow[];
}

export interface PublicAssistantConfig {
  enabled: boolean;
  turnLimit: number;
  rateLimitPerMinute: number;
  cooldownMs: number;
  cacheEnabled: boolean;
  quotaBackoffThreshold: number;
  maxOutputTokens: number;
  promptBudgetTokens: number;
}

export interface PublicTableOrderingState {
  status: 'active' | 'paused' | 'stopped';
  message: string | null;
  updatedAt: number | null;
  updatedBy: string | null;
  resumeAt: number | null;
  notice: QrOrderingNotice | null;
}

export interface PublicLoyaltyProgramConfig {
  status: 'enabled' | 'disabled' | 'preview';
  earnMode: string;
  pointsPerCurrency: number;
  currencyUnit: number;
  pointsPerOrder: number | null;
  redemption: {
    enabled: boolean;
    pointsPerUnit: number;
    currencyUnit: number;
    minPoints: number;
    maxPercent: number;
  };
}

export interface PublicTableResponse {
  table: {
    id: string;
    qr_slug: string;
    table_number: string | number | null;
  };
  tenant: {
    id: string;
    slug: string;
    name: string;
    currency: string;
  };
  location: {
    id: string;
    name: string;
    currency: string;
    qrOrderingStatus: 'active' | 'paused' | 'stopped';
    qrOrderingMessage: string | null;
    qrOrderingUpdatedAt: number | null;
    qrOrderingUpdatedBy: string | null;
  } | null;
  ordering: PublicTableOrderingState;
  categories: Array<{
    id: string;
    name: string;
    sort_order: number | null;
    translations?: Record<string, { name?: string; description?: string }>;
  }>;
  items: PublicTableItem[];
  pricing?: {
    currency: string;
    tax_rate_bps: number;
    service_charge_bps: number;
    card_surcharge_bps: number;
  };
  scheduling?: PublicTableScheduling;
  guest_profile?: PublicGuestProfile | null;
  assistant?: PublicAssistantConfig;
  loyalty?: PublicLoyaltyProgramConfig;
  cacheStatus?: 'network' | 'cache';
  cachedAt?: number;
}

export interface MenuReviewInviteItem {
  menuItemId: string;
  orderItemId: string;
  name: string;
  quantity: number;
  imageUrl: string | null;
  priceCents?: number | null;
}

export interface MenuReviewInvite {
  id: string;
  tenantId: string;
  orderId: string;
  status: string;
  expiresAt: number;
  items: MenuReviewInviteItem[];
  pendingItemIds: string[];
  completedItemIds: string[];
}

export interface MenuItemReviewEntry {
  id: string;
  rating: number;
  comment: string | null;
  locale: string;
  createdAt: number;
}

export type PublicPaymentMethod = 'cash' | 'card' | 'apple-pay' | 'google-pay';

export interface CreatePublicOrderItemInput {
  itemId: string;
  qty: number;
  notes?: string | null;
  modifiers?: string[];
}

export type NotificationChannel = 'email' | 'sms';
export type NotificationUpdateAction = 'opt_in' | 'opt_out';

export interface NotificationUpdatePayload {
  channel: NotificationChannel;
  action: NotificationUpdateAction;
  value?: string;
}

export interface GuestConsentPreference {
  marketing?: boolean;
  personalization?: boolean;
  schedule?: boolean;
  termsAcceptedAt?: string;
  notifications?: {
    email?: boolean;
    sms?: boolean;
  };
}

export interface GuestPreferencesPayload {
  saveProfile?: boolean;
  externalId?: string;
  loyaltyOptIn?: boolean;
  consent?: GuestConsentPreference;
  notificationUpdates?: NotificationUpdatePayload[];
  favoriteItemIds?: string[];
}

export interface ScheduleRequestPayload {
  requestedAt: number | string;
  windowLabel?: string;
}

export interface PublicPaymentPayload {
  method: PublicPaymentMethod;
  paymentMethodId?: string;
  paymentIntentId?: string;
  walletType?: 'apple-pay' | 'google-pay' | 'payment-request';
  paymentRequestPayload?: Record<string, unknown>;
  idempotencyKey?: string;
}

export interface CreatePublicOrderPayload {
  token: string;
  tableSlug?: string;
  qrSlug?: string;
  tenantSlug?: string;
  subdomain?: string;
  type?: 'dine-in' | 'takeaway' | 'delivery';
  guestCount?: number;
  guests?: number;
  notes?: string | null;
  tipCents?: number;
  tip_cents?: number;
  payment?: PublicPaymentPayload;
  payMethod?: PublicPaymentMethod;
  items: CreatePublicOrderItemInput[];
  clientRequestId?: string;
  guestName?: string;
  guestPhone?: string;
  guestEmail?: string;
  discountCents?: number;
  discount_cents?: number;
  discountCode?: string;
  discountReasonCode?: string;
  discountReasonNote?: string;
  guestPreferences?: GuestPreferencesPayload;
  schedule?: ScheduleRequestPayload;
  rewardToken?: string;
  rewardType?: string;
  loyaltyRedeemPoints?: number;
}

export interface CreatePublicOrderPayment {
  id?: string;
  method: 'card' | 'cash';
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'voided';
  reference?: string | null;
  displayMethod?: PublicPaymentMethod;
}

export interface CreatePublicOrderResponse {
  ok: true;
  id: string;
  code: string;
  status: string;
  total_cents: number;
  payment: CreatePublicOrderPayment;
  totals: Record<string, unknown>;
  placed_at: string;
  estimated_ready_at: string | null;
  estimated_prep_seconds: number | null;
  scheduled_for: string | null;
  scheduled_window?: {
    label: string;
    start: string;
    end: string;
  };
  loyalty_points_awarded: number;
  loyalty_points_redeemed: number;
  guest_token: string | null;
  guest_profile?: {
    external_id: string | null;
    loyalty_points: number;
    favorite_item_ids: string[];
  };
  throttle_override_applied: boolean;
}

export type CreatePublicOrderResult = CreatePublicOrderResponse | OfflineQueuedResult;

export interface PublicPaymentCapabilities {
  provider: string;
  publishableKey: string | null;
  wallets: {
    applePay: boolean;
    googlePay: boolean;
  };
}

export type TipMessageResolutionReason =
  | 'consent_blocked'
  | 'no_templates'
  | 'reused_fallback'
  | 'locale_fallback'
  | 'guardrail_subtotal'
  | 'guardrail_discount'
  | 'fatigue_suppressed';

export interface TipMessagePayload {
  id: string;
  body: string;
  tone: string;
  tags: string[];
  source: string;
  placeholders: Record<string, string | null | undefined>;
}

export interface TipMessageImpressionPayload {
  id: string;
  seenAt: string;
  cartFingerprint: string;
}

export interface TipMessageExperimentPayload {
  key: 'tip_message_variant';
  variant: string;
  payload: Record<string, unknown> | null;
}

export interface TipMessageCartItemPayload {
  itemId: string;
  quantity: number;
  modifierIds?: string[];
  notes?: string;
}

export interface TipMessageTipSelectionPayload {
  type: 'percentage' | 'fixed';
  valueCents: number;
}

export interface TipMessageResponse {
  ok: true;
  message: TipMessagePayload | null;
  impression: TipMessageImpressionPayload | null;
  reasons: TipMessageResolutionReason[];
  experiment: TipMessageExperimentPayload | null;
}

export interface FetchTipMessageParams {
  tenantSlug: string;
  cartFingerprint: string;
  language?: string;
  locationId?: string | null;
  guestProfileId?: string | null;
  anonSubjectId?: string | null;
  subjectId?: string | null;
  consentPersonalization?: boolean;
  disallowPersonalData?: boolean;
  tags?: string[];
  excludedMessageIds?: string[];
  cartValueCents?: number;
  cartSubtotalCents?: number;
  cartDiscountPercent?: number;
  cartItems?: TipMessageCartItemPayload[];
  tipSelection?: TipMessageTipSelectionPayload;
}

export interface UpdateTipMessageImpressionParams {
  tenantSlug: string;
  impressionId: string;
  orderId?: string | null;
  tipCents?: number | null;
  locationId?: string | null;
  paymentMethod?: string | null;
  result?: 'applied' | 'dismissed';
}

export interface UpdateTipMessageImpressionResponse {
  ok: true;
}

export interface ApplePaySessionRequest {
  validationUrl: string;
  domainName?: string;
  displayName?: string;
}

export interface ApplePaySessionResponse {
  ok: boolean;
  session?: Record<string, unknown>;
  [key: string]: unknown;
}

const RawPublicTableResponseSchema: ZodType<RawPublicTableResponse> = z.any();
const MenuReviewInviteItemSchema = z.object({
  menuItemId: z.string(),
  orderItemId: z.string(),
  name: z.string(),
  quantity: z.number(),
  imageUrl: z.string().nullable(),
  priceCents: z.number().nullable().optional(),
});
const MenuReviewInviteSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  orderId: z.string(),
  status: z.string(),
  expiresAt: z.number(),
  items: z.array(MenuReviewInviteItemSchema),
  pendingItemIds: z.array(z.string()),
  completedItemIds: z.array(z.string()),
});
const MenuReviewInviteResponseSchema = z
  .object({
    invite: MenuReviewInviteSchema,
  })
  .passthrough();
const MenuItemReviewEntrySchema = z.object({
  id: z.string(),
  rating: z.number(),
  comment: z.string().nullable(),
  locale: z.string(),
  createdAt: z.number(),
});
const MenuItemReviewListSchema = z
  .object({
    reviews: z.array(MenuItemReviewEntrySchema),
    nextCursor: z.string().nullable(),
  })
  .passthrough();
const SubmitMenuReviewResponseSchema = z
  .object({
    review: MenuItemReviewEntrySchema,
    stats: z
      .object({
        menuItemId: z.string(),
        averageRating: z.number().nullable(),
        ratingsCount: z.number(),
      })
      .nullable(),
    invite: MenuReviewInviteSchema,
  })
  .passthrough();
const PublicPaymentCapabilitiesSchema: ZodType<PublicPaymentCapabilities> = z.object({
  provider: z.string(),
  publishableKey: z.string().nullable(),
  wallets: z.object({
    applePay: z.boolean(),
    googlePay: z.boolean(),
  }),
});
const ApplePaySessionResponseSchema: ZodType<ApplePaySessionResponse> = z
  .object({
    ok: z.boolean(),
    session: z.record(z.unknown()).optional(),
  })
  .passthrough();

const TipMessageSchema: ZodType<TipMessagePayload> = z.object({
  id: z.string(),
  body: z.string(),
  tone: z.string(),
  tags: z.array(z.string()),
  source: z.string(),
  placeholders: z.record(z.string().optional()).default({}),
});

const TipMessageImpressionSchema: ZodType<TipMessageImpressionPayload> = z.object({
  id: z.string(),
  seenAt: z.string(),
  cartFingerprint: z.string(),
});

const TipMessageExperimentSchema: ZodType<TipMessageExperimentPayload> = z.object({
  key: z.literal('tip_message_variant'),
  variant: z.string(),
  payload: z.record(z.unknown()).nullable(),
});

const TipMessageResponseSchema: ZodType<TipMessageResponse> = z.object({
  ok: z.literal(true),
  message: TipMessageSchema.nullable(),
  impression: TipMessageImpressionSchema.nullable(),
  reasons: z
    .array(
      z.union([
        z.literal('consent_blocked'),
        z.literal('no_templates'),
        z.literal('reused_fallback'),
        z.literal('locale_fallback'),
        z.literal('guardrail_subtotal'),
        z.literal('guardrail_discount'),
        z.literal('fatigue_suppressed'),
      ]),
    )
    .default([]),
  experiment: TipMessageExperimentSchema.nullable(),
});

const UpdateTipMessageImpressionResponseSchema: ZodType<UpdateTipMessageImpressionResponse> = z.object({
  ok: z.literal(true),
});

interface RawPublicAvailabilityWindow {
  start?: number | null;
  end?: number | null;
}

interface RawPublicAvailabilityReason {
  code?: string | null;
  scope?: string | null;
  message?: string | null;
}

interface RawPublicAvailability {
  status?: string | null;
  is_available?: boolean;
  isAvailable?: boolean;
  reasons?: unknown;
  next_available_at?: number | null;
  nextAvailableAt?: number | null;
  sold_out_until?: number | null;
  soldOutUntil?: number | null;
  auto_expire_at?: number | null;
  autoExpireAt?: number | null;
  category_auto_expire_at?: number | null;
  categoryAutoExpireAt?: number | null;
  current_window?: RawPublicAvailabilityWindow | null;
  currentWindow?: RawPublicAvailabilityWindow | null;
  upcoming_windows?: RawPublicAvailabilityWindow[] | null;
  upcomingWindows?: RawPublicAvailabilityWindow[] | null;
}

interface RawPublicTableItem {
  id: string;
  category_id: string | null;
  name: string;
  description: string | null;
  price_cents: number;
  is_available: boolean;
  availability?: RawPublicAvailability | null;
  image_url: string | null;
  image_alt_text: string | null;
  image_spice_level: string | null;
  modifiers?: PublicTableItemModifier[] | null;
  metadata?: Record<string, unknown> | null;
  taste_profile?: unknown;
  tasteProfile?: unknown;
  tags?: unknown;
  average_rating?: number | string | null;
  review_count?: number | string | null;
}

interface RawPublicTableResponse {
  table: {
    id: string;
    qr_slug: string;
    table_number: string | number | null;
  };
  tenant: {
    id: string;
    slug: string;
    name: string;
    currency: string;
  };
  location: {
    id: string;
    name: string;
    currency: string;
    qr_ordering_status?: string | null;
    qr_ordering_message?: string | null;
    qr_ordering_updated_at?: number | string | null;
    qr_ordering_updated_by?: string | null;
  } | null;
  categories: Array<{
    id: string;
    name: string;
    sort_order: number | null;
  }>;
  items: RawPublicTableItem[];
  pricing?: {
    currency?: string | null;
    tax_rate_bps?: number | string | null;
    service_charge_bps?: number | string | null;
    card_surcharge_bps?: number | string | null;
  } | null;
  scheduling?: {
    timezone?: string | null;
    windows?: Array<{
      id?: string | null;
      label?: string | null;
      start?: number | null;
      end?: number | null;
    }> | null;
  } | null;
  guest_profile?: unknown;
  ordering?: {
    status?: string | null;
    message?: string | null;
    updated_at?: number | string | null;
    updated_by?: string | null;
    resume_at?: number | string | null;
    notice?: unknown;
  } | null;
}

const defaultAvailabilityState = (isAvailable = false): MenuAvailabilityState => ({
  status: isAvailable ? 'available' : 'inactive',
  isAvailable,
  reasons: [],
  nextAvailableAt: null,
  currentWindow: null,
  upcomingWindows: [],
  soldOutUntil: null,
  autoExpireAt: null,
  categoryAutoExpireAt: null,
});

const coerceNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const coerceStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<string>();
  const result: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string') {
      continue;
    }
    const trimmed = entry.trim();
    if (!trimmed) {
      continue;
    }
    const normalized = trimmed.toLowerCase();
    if (seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
};

const coerceNumberRecord = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const record = value as Record<string, unknown>;
  const result: Record<string, number> = {};
  for (const [key, raw] of Object.entries(record)) {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      result[key] = Number(raw.toFixed(3));
      continue;
    }
    if (typeof raw === 'string' && raw.trim().length > 0) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        result[key] = Number(parsed.toFixed(3));
      }
    }
  }
  return result;
};

const normaliseTasteProfileValue = (input: unknown): PublicTasteProfile | null => {
  if (!input) {
    return null;
  }
  if (Array.isArray(input)) {
    const tags = coerceStringArray(input);
    return tags.length ? { tags } : null;
  }
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) {
      return null;
    }
    return { tags: [trimmed.toLowerCase()] };
  }
  if (typeof input === 'object') {
    const record = input as Record<string, unknown>;
    const tagCandidates = [
      record.tags,
      record.taste_tags,
      record.tasteTags,
      record.flavorTags,
    ];
    let tags: string[] = [];
    for (const candidate of tagCandidates) {
      const parsed = coerceStringArray(candidate);
      if (parsed.length) {
        tags = parsed;
        break;
      }
    }
    const cuisinesCandidate = record.cuisines ?? record.cuisineTags ?? record.cuisine_tags;
    const cuisines = coerceStringArray(cuisinesCandidate);
    const heatCandidate =
      typeof record.heat === 'string'
        ? record.heat
        : typeof record.spiceLevel === 'string'
        ? record.spiceLevel
        : typeof record.tasteHeat === 'string'
        ? record.tasteHeat
        : undefined;
    const heat = heatCandidate && heatCandidate.trim().length > 0 ? heatCandidate.trim() : undefined;
    const tagScoresCandidate = record.tagScores ?? record.tag_scores ?? record.tasteTagScores;
    const cuisineAffinityCandidate =
      record.cuisineAffinity ?? record.cuisine_affinity ?? record.tasteCuisineAffinity;
    const tagScores = coerceNumberRecord(tagScoresCandidate);
    const cuisineAffinity = coerceNumberRecord(cuisineAffinityCandidate);
    const updatedAtCandidate = record.updatedAt ?? record.updated_at;
    const updatedAt =
      typeof updatedAtCandidate === 'number'
        ? updatedAtCandidate
        : typeof updatedAtCandidate === 'string' && updatedAtCandidate.trim().length > 0
        ? Number(updatedAtCandidate)
        : undefined;
    const source =
      record.source === 'stored' || record.source === 'derived' || record.source === 'fallback'
        ? record.source
        : undefined;
    if (
      !tags.length &&
      !heat &&
      cuisines.length === 0 &&
      Object.keys(tagScores).length === 0 &&
      Object.keys(cuisineAffinity).length === 0
    ) {
      return null;
    }
    return {
      ...(tags.length ? { tags } : {}),
      ...(heat ? { heat } : {}),
      ...(cuisines.length ? { cuisines } : {}),
      ...(Object.keys(tagScores).length ? { tagScores } : {}),
      ...(Object.keys(cuisineAffinity).length ? { cuisineAffinity } : {}),
      ...(Number.isFinite(updatedAt) ? { updatedAt: Number(updatedAt) } : {}),
      ...(source ? { source } : {}),
    };
  }
  return null;
};

const normalizeTrimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeQrOrderingStatus = (value: unknown): 'active' | 'paused' | 'stopped' => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'paused' || normalized === 'stopped') {
      return normalized as 'paused' | 'stopped';
    }
  }
  return 'active';
};

const normaliseBps = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.round(value));
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.round(parsed));
    }
  }
  return 0;
};

const normaliseAvailabilityWindow = (input: unknown): MenuAvailabilityWindow => {
  if (!input || typeof input !== 'object') {
    return { start: null, end: null };
  }
  const record = input as Record<string, unknown>;
  return {
    start: coerceNullableNumber(record.start),
    end: coerceNullableNumber(record.end),
  };
};

const normaliseAvailabilityReasons = (input: unknown): MenuAvailabilityReason[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  return input
    .map((entry) => {
      if (typeof entry === 'string') {
        return { code: entry, scope: 'item' as const };
      }
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const record = entry as RawPublicAvailabilityReason;
      const code = typeof record.code === 'string' ? record.code : null;
      if (!code) {
        return null;
      }
      const scope = typeof record.scope === 'string' ? record.scope : 'item';
      const message = typeof record.message === 'string' ? record.message : null;
      return { code, scope: scope as MenuAvailabilityReason['scope'], message: message ?? undefined };
    })
    .filter((reason): reason is MenuAvailabilityReason => reason !== null);
};

const normaliseAvailabilityState = (input: unknown, fallbackAvailable: boolean): MenuAvailabilityState => {
  if (!input || typeof input !== 'object') {
    return defaultAvailabilityState(fallbackAvailable);
  }

  const record = input as Record<string, unknown>;
  const rawStatus = (record as { status?: unknown }).status;
  const status = typeof rawStatus === 'string'
    ? (rawStatus as MenuAvailabilityState['status'])
    : fallbackAvailable
      ? 'available'
      : 'inactive';

  const availabilityFlags = record as { is_available?: unknown; isAvailable?: unknown };
  let isAvailable = fallbackAvailable;
  if (typeof availabilityFlags.is_available === 'boolean') {
    isAvailable = availabilityFlags.is_available;
  } else if (typeof availabilityFlags.isAvailable === 'boolean') {
    isAvailable = availabilityFlags.isAvailable;
  }

  const rawReasons = (record as { reasons?: unknown }).reasons;
  const rawNextAvailable =
    (record as { next_available_at?: unknown }).next_available_at ??
    (record as { nextAvailableAt?: unknown }).nextAvailableAt;
  const rawSoldOut =
    (record as { sold_out_until?: unknown }).sold_out_until ??
    (record as { soldOutUntil?: unknown }).soldOutUntil;
  const rawAutoExpire =
    (record as { auto_expire_at?: unknown }).auto_expire_at ??
    (record as { autoExpireAt?: unknown }).autoExpireAt;
  const rawCategoryAutoExpire =
    (record as { category_auto_expire_at?: unknown }).category_auto_expire_at ??
    (record as { categoryAutoExpireAt?: unknown }).categoryAutoExpireAt;
  const rawCurrentWindow =
    (record as { current_window?: unknown }).current_window ??
    (record as { currentWindow?: unknown }).currentWindow;
  const rawUpcoming =
    (record as { upcoming_windows?: unknown }).upcoming_windows ??
    (record as { upcomingWindows?: unknown }).upcomingWindows;

  return {
    status,
    isAvailable,
    reasons: normaliseAvailabilityReasons(rawReasons),
    nextAvailableAt: typeof rawNextAvailable === 'number' ? rawNextAvailable : null,
    currentWindow: rawCurrentWindow ? normaliseAvailabilityWindow(rawCurrentWindow) : null,
    upcomingWindows: Array.isArray(rawUpcoming)
      ? (rawUpcoming as RawPublicAvailabilityWindow[]).map(normaliseAvailabilityWindow)
      : [],
    soldOutUntil: typeof rawSoldOut === 'number' ? rawSoldOut : null,
    autoExpireAt: typeof rawAutoExpire === 'number' ? rawAutoExpire : null,
    categoryAutoExpireAt: typeof rawCategoryAutoExpire === 'number' ? rawCategoryAutoExpire : null,
  };
};

const normalisePublicItem = (item: RawPublicTableItem): PublicTableItem => {
  const availability = normaliseAvailabilityState(item.availability, item.is_available !== false);
  const metadata =
    item.metadata && typeof item.metadata === 'object' && !Array.isArray(item.metadata)
      ? { ...(item.metadata as Record<string, unknown>) }
      : undefined;
  const taste_profile = normaliseTasteProfileValue(
    item.taste_profile ??
      item.tasteProfile ??
      (metadata && 'taste_profile' in metadata
        ? (metadata as { taste_profile?: unknown }).taste_profile
        : undefined) ??
      (metadata && 'tasteProfile' in metadata
        ? (metadata as { tasteProfile?: unknown }).tasteProfile
        : undefined),
  );
  const tagSources: unknown[] = [
    item.tags,
    metadata && 'taste_tags' in metadata
      ? (metadata as { taste_tags?: unknown }).taste_tags
      : undefined,
    metadata && 'tags' in metadata ? (metadata as { tags?: unknown }).tags : undefined,
  ];
  let tags: string[] = [];
  for (const candidate of tagSources) {
    const parsed = coerceStringArray(candidate);
    if (parsed.length) {
      tags = parsed;
      break;
    }
  }
  const payload: PublicTableItem = {
    id: item.id,
    category_id: item.category_id,
    name: item.name,
    description: item.description,
    price_cents: item.price_cents,
    is_available: item.is_available !== false,
    availability,
    image_url: item.image_url,
    image_alt_text: item.image_alt_text,
    image_spice_level: item.image_spice_level,
    modifiers: Array.isArray(item.modifiers) ? item.modifiers.slice() : [],
    margin_cents: typeof item.margin_cents === 'number' ? item.margin_cents : null,
    prep_minutes: typeof item.prep_minutes === 'number' ? item.prep_minutes : null,
    station: typeof item.station === 'string' ? item.station : null,
    is_new: typeof item.is_new === 'boolean' ? item.is_new : null,
  };
  if (metadata && Object.keys(metadata).length > 0) {
    payload.metadata = metadata;
  }
  if (tags.length) {
    payload.tags = tags;
  }
  if (taste_profile) {
    payload.taste_profile = taste_profile;
  }
  return payload;
};

const normaliseGuestProfile = (input: unknown): PublicGuestProfile | null => {
  if (!input || typeof input !== 'object') {
    return null;
  }
  const record = input as Record<string, unknown>;
  const externalId = typeof record.external_id === 'string' ? record.external_id : null;
  const name = typeof record.name === 'string' ? record.name : null;
  const loyaltyPointsRaw = (record.loyalty_points ?? record.loyaltyPoints) as unknown;
  const loyaltyPoints = typeof loyaltyPointsRaw === 'number' && Number.isFinite(loyaltyPointsRaw)
    ? Math.max(0, Math.round(loyaltyPointsRaw))
    : 0;
  const favoriteItemsRaw = (record.favorite_item_ids ?? record.favoriteItemIds) as unknown;
  const favorite_item_ids = Array.isArray(favoriteItemsRaw)
    ? (favoriteItemsRaw as unknown[])
        .map((value) => (typeof value === 'string' ? value : null))
        .filter((value): value is string => value !== null)
    : [];
  const lastSnapshot = record.last_order_snapshot ?? record.lastOrderSnapshot;
  const consent = record.consent ?? record.guest_consent ?? null;
  const scheduledPreferences = record.scheduled_preferences ?? record.scheduledOrderPreferences ?? null;
  const tasteCandidates: unknown[] = [
    record.taste_profile,
    (record as { tasteProfile?: unknown }).tasteProfile,
    (record as { taste_tags?: unknown }).taste_tags,
    (record as { tasteTags?: unknown }).tasteTags,
  ];
  let taste_profile: PublicTasteProfile | null = null;
  for (const candidate of tasteCandidates) {
    const parsed = normaliseTasteProfileValue(candidate ?? null);
    if (parsed) {
      taste_profile = parsed;
      break;
    }
  }
  const tasteTags = coerceStringArray(
    (record.taste_tags ?? (record as { tasteTags?: unknown }).tasteTags) ?? [],
  );
  const tasteCuisines = coerceStringArray(
    (record.taste_cuisines ?? (record as { tasteCuisines?: unknown }).tasteCuisines) ?? [],
  );
  const heatCandidate = record.taste_heat ?? (record as { tasteHeat?: unknown }).tasteHeat ?? null;
  const tasteHeat =
    typeof heatCandidate === 'string' && heatCandidate.trim().length > 0
      ? heatCandidate.trim().toLowerCase()
      : null;
  const tasteTagScores =
    coerceNumberRecord(
      record.taste_tag_scores ?? (record as { tasteTagScores?: unknown }).tasteTagScores ?? {},
    );
  const tasteCuisineAffinity =
    coerceNumberRecord(
      record.taste_cuisine_affinity ??
        (record as { tasteCuisineAffinity?: unknown }).tasteCuisineAffinity ??
        {},
    );
  const tasteProfileUpdatedAt = coerceNullableNumber(
    record.taste_profile_updated_at ??
      (record as { tasteProfileUpdatedAt?: unknown }).tasteProfileUpdatedAt ??
      null,
  );
  const reward = record.reward ?? (record as { personalizedReward?: unknown }).personalizedReward ?? null;
  return {
    external_id: externalId,
    name,
    loyalty_points: loyaltyPoints,
    favorite_item_ids,
    last_order_snapshot:
      lastSnapshot && typeof lastSnapshot === 'object'
        ? (lastSnapshot as Record<string, unknown>)
        : null,
    consent:
      consent && typeof consent === 'object'
        ? (consent as Record<string, unknown>)
        : null,
    scheduled_preferences:
      scheduledPreferences && typeof scheduledPreferences === 'object'
        ? (scheduledPreferences as Record<string, unknown>)
        : null,
    ...(taste_profile ? { taste_profile } : {}),
    ...(tasteTags.length ? { taste_tags: tasteTags } : {}),
    ...(tasteCuisines.length ? { taste_cuisines: tasteCuisines } : {}),
    ...(tasteHeat ? { taste_heat: tasteHeat } : {}),
    ...(Object.keys(tasteTagScores).length ? { taste_tag_scores: tasteTagScores } : {}),
    ...(Object.keys(tasteCuisineAffinity).length
      ? { taste_cuisine_affinity: tasteCuisineAffinity }
      : {}),
    ...(typeof tasteProfileUpdatedAt === 'number'
      ? { taste_profile_updated_at: tasteProfileUpdatedAt }
      : {}),
    ...(reward && typeof reward === 'object'
      ? {
          reward: (() => {
            const r = reward as Record<string, unknown>;
            const type = typeof r.type === 'string' ? r.type : 'reward';
            const token = typeof r.token === 'string' ? r.token : '';
            const description = typeof r.description === 'string' ? (r.description as string) : null;
            return token ? { type, token, description } : null;
          })(),
        }
      : {}),
  };
};

const normaliseScheduling = (input: RawPublicTableResponse['scheduling']): PublicTableScheduling | undefined => {
  if (!input) {
    return undefined;
  }
  const timezoneValue =
    typeof input.timezone === 'string' && input.timezone.trim().length > 0
      ? input.timezone
      : 'UTC';
  const windowsRaw = Array.isArray(input.windows) ? input.windows : [];
  const windows: PublicSchedulingWindow[] = windowsRaw
    .map((window) => {
      if (!window) {
        return null;
      }
      const start = typeof window.start === 'number' ? window.start : null;
      const end = typeof window.end === 'number' ? window.end : null;
      if (start === null || end === null) {
        return null;
      }
      const label =
        typeof window.label === 'string' && window.label.trim().length > 0
          ? window.label
          : typeof window.id === 'string'
          ? window.id
          : new Date(start).toLocaleTimeString();
      const id =
        typeof window.id === 'string' && window.id.trim().length > 0
          ? window.id
          : `slot-${start}`;
      return {
        id,
        label,
        start,
        end,
      };
    })
    .filter((window): window is PublicSchedulingWindow => window !== null);

  return {
    timezone: timezoneValue,
    windows,
  };
};

const normaliseAssistantConfig = (input: unknown): PublicAssistantConfig | undefined => {
  if (!input || typeof input !== 'object') {
    return undefined;
  }
  const record = input as Record<string, unknown>;
  const enabled = Boolean(record.enabled);
  const turnLimitRaw = Number(record.turnLimit ?? record.turn_limit ?? 0);
  const rateLimitRaw = Number(record.rateLimitPerMinute ?? record.rate_limit_per_minute ?? 0);
  const cooldownRaw = Number(record.cooldownMs ?? record.cooldown_ms ?? 0);
  const quotaThresholdRaw =
    typeof record.quotaBackoffThreshold === 'number'
      ? record.quotaBackoffThreshold
      : typeof record.quota_backoff_threshold === 'number'
      ? record.quota_backoff_threshold
      : undefined;
  const maxTokensRaw = Number(record.maxOutputTokens ?? record.max_output_tokens ?? 0);
  const promptBudgetRaw = Number(record.promptBudgetTokens ?? record.prompt_budget_tokens ?? 0);

  const normalised: PublicAssistantConfig = {
    enabled,
    turnLimit: Number.isFinite(turnLimitRaw) ? Math.max(1, Math.trunc(turnLimitRaw)) : 12,
    rateLimitPerMinute: Number.isFinite(rateLimitRaw) ? Math.max(0, Math.trunc(rateLimitRaw)) : 0,
    cooldownMs: Number.isFinite(cooldownRaw) ? Math.max(0, Math.trunc(cooldownRaw)) : 0,
    cacheEnabled: Boolean(record.cacheEnabled ?? record.cache_enabled),
    quotaBackoffThreshold:
      typeof quotaThresholdRaw === 'number' && Number.isFinite(quotaThresholdRaw)
        ? Math.min(1, Math.max(0, quotaThresholdRaw))
        : 0.9,
    maxOutputTokens: Number.isFinite(maxTokensRaw) ? Math.max(0, Math.trunc(maxTokensRaw)) : 0,
    promptBudgetTokens: Number.isFinite(promptBudgetRaw)
      ? Math.max(0, Math.trunc(promptBudgetRaw))
      : 0,
  };

  return normalised;
};

const normalisePublicTableResponse = (payload: RawPublicTableResponse): PublicTableResponse => {
  const normalizedLocation = (() => {
    if (!payload.location) {
      return null;
    }
    return {
      id: payload.location.id,
      name: payload.location.name,
      currency: payload.location.currency,
      qrOrderingStatus: normalizeQrOrderingStatus(payload.location.qr_ordering_status),
      qrOrderingMessage: normalizeTrimmedString(payload.location.qr_ordering_message),
      qrOrderingUpdatedAt: coerceNullableNumber(payload.location.qr_ordering_updated_at),
      qrOrderingUpdatedBy: normalizeTrimmedString(payload.location.qr_ordering_updated_by),
    };
  })();

  const resolvedOrderingStatus = normalizeQrOrderingStatus(
    payload.ordering?.status ?? normalizedLocation?.qrOrderingStatus ?? 'active',
  );
  const orderingMessage =
    normalizeTrimmedString(payload.ordering?.message) ?? normalizedLocation?.qrOrderingMessage ?? null;
  const orderingUpdatedAt =
    coerceNullableNumber(payload.ordering?.updated_at) ??
    (normalizedLocation?.qrOrderingUpdatedAt ?? null);
  const orderingUpdatedBy =
    normalizeTrimmedString(payload.ordering?.updated_by) ??
    normalizedLocation?.qrOrderingUpdatedBy ??
    null;
  const orderingNotice =
    resolvedOrderingStatus === 'active'
      ? null
      : parseQrOrderingNotice(
          payload.ordering?.notice ?? normalizedLocation?.qrOrderingMessage ?? null,
        );
  const orderingResumeAt =
    coerceNullableNumber(payload.ordering?.resume_at) ??
    (orderingNotice?.resumeAt ?? null);

  const pricing = (() => {
    const baseCurrency =
      payload.tenant?.currency && payload.tenant.currency.trim().length > 0 ? payload.tenant.currency : 'USD';
    const record = payload.pricing ?? null;
    if (!record) {
      return {
        currency: baseCurrency,
        tax_rate_bps: 0,
        service_charge_bps: 0,
      };
    }
    const currency =
      typeof record.currency === 'string' && record.currency.trim().length > 0 ? record.currency : baseCurrency;
    return {
      currency,
      tax_rate_bps: normaliseBps(record.tax_rate_bps),
      service_charge_bps: normaliseBps(record.service_charge_bps),
      card_surcharge_bps: normaliseBps(record.card_surcharge_bps),
    };
  })();

  const loyaltyConfig = (() => {
    const source = payload.loyalty;
    if (!source || typeof source !== 'object') {
      return undefined;
    }
    const redemptionSource = (source as { redemption?: Record<string, unknown> }).redemption ?? {};
    const statusCandidate = (source as { status?: string }).status;
    const earnModeCandidate = (source as { earnMode?: string }).earnMode;
    return {
      status:
        statusCandidate === 'enabled' || statusCandidate === 'preview' ? statusCandidate : 'disabled',
      earnMode: typeof earnModeCandidate === 'string' ? earnModeCandidate : 'per_spend',
      pointsPerCurrency: Number(
        (source as { pointsPerCurrency?: unknown }).pointsPerCurrency ?? 0,
      ),
      currencyUnit: Number((source as { currencyUnit?: unknown }).currencyUnit ?? 100),
      pointsPerOrder:
        typeof (source as { pointsPerOrder?: unknown }).pointsPerOrder === 'number'
          ? ((source as { pointsPerOrder: number }).pointsPerOrder ?? null)
          : null,
      redemption: {
        enabled: Boolean((redemptionSource as { enabled?: unknown }).enabled),
        pointsPerUnit: Number(
          (redemptionSource as { pointsPerUnit?: unknown }).pointsPerUnit ?? 0,
        ),
        currencyUnit: Number(
          (redemptionSource as { currencyUnit?: unknown }).currencyUnit ?? 100,
        ),
        minPoints: Math.max(
          0,
          Math.floor((redemptionSource as { minPoints?: unknown }).minPoints ?? 0),
        ),
        maxPercent: Math.max(
          0,
          Math.min(
            100,
            Math.floor((redemptionSource as { maxPercent?: unknown }).maxPercent ?? 100),
          ),
        ),
      },
    };
  })();

  return {
    table: payload.table,
    tenant: payload.tenant,
    location: normalizedLocation,
    categories: Array.isArray(payload.categories) ? payload.categories : [],
    items: Array.isArray(payload.items) ? payload.items.map(normalisePublicItem) : [],
    pricing,
    scheduling: normaliseScheduling(payload.scheduling),
    ordering: {
      status: resolvedOrderingStatus,
      message: orderingMessage,
      updatedAt: orderingUpdatedAt,
      updatedBy: orderingUpdatedBy,
      resumeAt: orderingResumeAt,
      notice: orderingNotice,
    },
    guest_profile: normaliseGuestProfile(payload.guest_profile ?? null),
    assistant: normaliseAssistantConfig(payload.assistant),
    loyalty: loyaltyConfig,
  };
};
const normaliseQueueId = (value: unknown): string => {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return `queued-${Date.now().toString(36)}`;
};

const isQueuedPayload = (value: unknown): value is { queued: true; queueId?: unknown; meta?: unknown } =>
  typeof value === 'object' && value !== null && (value as { queued?: unknown }).queued === true;

function buildHeaders(tenantSlug: string, includeJson = false): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Tenant': tenantSlug,
  };
  if (includeJson) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

interface GetPublicTableOptions {
  signal?: AbortSignal;
  guestToken?: string;
}

const resolveAnonymousGuestId = (): string | null => {
  const storageKey = 'braise_guest_session_id';
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const existing = window.localStorage.getItem(storageKey);
    if (existing && existing.trim().length > 0) {
      return existing;
    }
    const generated = `g_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    window.localStorage.setItem(storageKey, generated);
    return generated;
  } catch {
    return null;
  }
};

export async function getPublicTable(
  tenantSlug: string,
  slug: string,
  token: string,
  options: GetPublicTableOptions = {},
): Promise<PublicTableResponse> {
  if (!token) {
    throw new Error('Deep link token is required to load table data.');
  }

  const { signal, guestToken } = options;
  const params = new URLSearchParams();
  params.set('token', token);
  if (guestToken && guestToken.trim().length > 0) {
    params.set('guest_token', guestToken.trim());
  }
  const queryString = params.toString();

  const cacheNamespace =
    guestToken && guestToken.length > 0 ? `public-table:${slug}:${guestToken}` : `public-table:${slug}`;
  try {
    const apiFetch = await resolveApiFetch();
    const anonymousGuestId = resolveAnonymousGuestId();
    const response = await apiFetch(`/public/table/${encodeURIComponent(slug)}?${queryString}`, {
      method: 'GET',
      headers: {
        ...buildHeaders(tenantSlug),
        'X-QR-Token': token,
        ...(guestToken ? { 'X-Guest-Token': guestToken } : {}),
        ...(anonymousGuestId ? { 'X-Session-Id': anonymousGuestId } : {}),
      },
      signal,
    });

    await assertOk(response);
    const payload = await parseJson(response, RawPublicTableResponseSchema);
    const normalised = normalisePublicTableResponse(payload);
    const cachedAt = Date.now();
    saveOfflineSnapshot(cacheNamespace, tenantSlug, { ...normalised, cachedAt }, OFFLINE_PUBLIC_TABLE_VERSION);
    return { ...normalised, cacheStatus: 'network', cachedAt };
  } catch (error) {
    const fallback = loadOfflineSnapshot<PublicTableResponse>(cacheNamespace, tenantSlug, OFFLINE_PUBLIC_TABLE_VERSION);
    if (fallback) {
      return { ...fallback.data, cacheStatus: 'cache', cachedAt: fallback.cachedAt };
    }
    throw error;
  }
}

export async function createPublicOrder(
  tenantSlug: string,
  payload: CreatePublicOrderPayload,
  signal?: AbortSignal,
): Promise<CreatePublicOrderResult> {
  if (!payload.token) {
    throw new Error('Deep link token is required to place an order.');
  }

  const clientRequestId = payload.clientRequestId ?? generateClientRequestId('public');
  const requestPayload = { ...payload, clientRequestId };
  const result = await jsonRequest<unknown>('/public/orders', {
    method: 'POST',
    headers: {
      ...buildHeaders(tenantSlug, true),
      'X-QR-Token': payload.token,
    },
    body: requestPayload,
    signal,
    schema: z.unknown(),
  });

  if (isQueuedPayload(result)) {
    return {
      queued: true,
      queueId: normaliseQueueId(result.queueId),
      meta: result.meta,
    } as OfflineQueuedResult;
  }

  return result as CreatePublicOrderResponse;
}

export async function getPublicPaymentCapabilities(): Promise<PublicPaymentCapabilities> {
  return jsonRequest<PublicPaymentCapabilities>('/public/payments/capabilities', {
    method: 'GET',
    schema: PublicPaymentCapabilitiesSchema,
  });
}

export async function requestApplePaySession(
  payload: ApplePaySessionRequest,
  signal?: AbortSignal,
): Promise<Record<string, unknown>> {
  const result = await jsonRequest<ApplePaySessionResponse>('/public/payments/apple-pay/session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: payload,
    signal,
    schema: ApplePaySessionResponseSchema,
  });

  if (!result.ok || !result.session) {
    throw new Error('Apple Pay session response missing session payload.');
  }
  return result.session;
}

export async function fetchTipMessage(
  params: FetchTipMessageParams,
  signal?: AbortSignal,
): Promise<TipMessageResponse> {
  const { tenantSlug, ...body } = params;
  return jsonRequest<TipMessageResponse>('/public/tips/message', {
    method: 'POST',
    headers: buildHeaders(tenantSlug, true),
    body,
    signal,
    schema: TipMessageResponseSchema,
  });
}

export async function updateTipMessageImpression(
  params: UpdateTipMessageImpressionParams,
  signal?: AbortSignal,
): Promise<UpdateTipMessageImpressionResponse> {
  const { tenantSlug, impressionId, orderId, tipCents, paymentMethod, locationId, result } = params;
  const payload: Record<string, unknown> = {
    tenantSlug,
    impressionId,
  };
  if (orderId && orderId.trim().length > 0) {
    payload.orderId = orderId;
  }
  if (typeof tipCents === 'number' && Number.isFinite(tipCents) && tipCents >= 0) {
    payload.tipCents = Math.round(tipCents);
  }
  if (locationId && locationId.trim().length > 0) {
    payload.locationId = locationId;
  }
  if (paymentMethod && paymentMethod.trim().length > 0) {
    payload.paymentMethod = paymentMethod;
  }
  if (result) {
    payload.result = result;
  }
  return jsonRequest<UpdateTipMessageImpressionResponse>('/public/tips/impression', {
    method: 'PATCH',
    headers: buildHeaders(tenantSlug, true),
    body: payload,
    signal,
    schema: UpdateTipMessageImpressionResponseSchema,
  });
}

const RecommendationImpressionResponseSchema = z.object({
  ok: z.boolean().optional(),
  id: z.string().uuid(),
});

export interface RecommendationImpressionPayload {
  tenantSlug: string;
  locationId?: string | null;
  tableId?: string | null;
  qrSlug?: string | null;
  profileSource?: string | null;
  consentPersonalization: boolean;
  tags?: string[];
  cuisines?: string[];
  heat?: string | null;
  tagScores?: Record<string, number>;
  cuisineAffinity?: Record<string, number>;
  locale?: string | null;
  recommendedItemIds: string[];
  recommendedBundleIds?: string[];
  customerExternalId?: string | null;
  guestProfileId?: string | null;
  sessionId?: string | null;
  impressionKey?: string | null;
  requestUrl?: string | null;
  modelVersion?: string | null;
  surface?: string | null;
  holdout?: boolean;
}

export async function recordRecommendationImpression(
  params: RecommendationImpressionPayload,
  signal?: AbortSignal,
): Promise<{ id: string }> {
  const response = await jsonRequest('/public/recommendations/impression', {
    method: 'POST',
    headers: buildHeaders(params.tenantSlug, true),
    body: params,
    signal,
    schema: RecommendationImpressionResponseSchema,
  });

  return { id: response.id };
}

const RecommendationActionResponseSchema = z.object({
  ok: z.boolean(),
});

export interface RecommendationActionPayload {
  tenantSlug: string;
  impressionId: string;
  action: 'add_item' | 'add_bundle' | 'open_customization' | 'dismissed' | 'order_item' | 'not_interested';
  menuItemId?: string;
  bundleId?: string;
  reason?: string;
  metadata?: Record<string, unknown>;
  guestProfileId?: string | null;
  quantity?: number;
  orderId?: string | null;
  slot?: number;
  surface?: string | null;
  modelVersion?: string | null;
  holdout?: boolean;
}

export async function recordRecommendationAction(
  params: RecommendationActionPayload,
  signal?: AbortSignal,
): Promise<void> {
  await jsonRequest('/public/recommendations/action', {
    method: 'POST',
    headers: buildHeaders(params.tenantSlug, true),
    body: params,
    signal,
    schema: RecommendationActionResponseSchema,
  });
}

const SyncGuestProfileResponseSchema = z.object({
  ok: z.boolean(),
  guestToken: z.string(),
  favorites: z.array(z.string()),
  consent: z
    .object({
      personalization: z.boolean().optional(),
    })
    .optional(),
});

export interface SyncGuestProfileParams {
  tenantSlug: string;
  guestToken?: string | null;
  guestPhone?: string | null;
  guestName?: string | null;
  favorites?: string[];
  consentPersonalization?: boolean;
}

export async function syncGuestProfile(
  params: SyncGuestProfileParams,
  signal?: AbortSignal,
): Promise<{ guestToken: string; favorites: string[]; consentPersonalization?: boolean }> {
  const { tenantSlug, ...body } = params;
  const payload: Record<string, unknown> = {};
  if (body.guestToken) {
    payload.guestToken = body.guestToken;
  }
  if (body.guestPhone) {
    payload.guestPhone = body.guestPhone;
  }
  if (body.guestName) {
    payload.guestName = body.guestName;
  }
  if (body.favorites) {
    payload.favorites = body.favorites;
  }
  if (typeof body.consentPersonalization === 'boolean') {
    payload.consent = { personalization: body.consentPersonalization };
  }

  const response = await jsonRequest<z.infer<typeof SyncGuestProfileResponseSchema>>(
    '/public/guest-profiles/sync',
    {
      method: 'POST',
      headers: buildHeaders(tenantSlug, true),
      body: payload,
      signal,
      schema: SyncGuestProfileResponseSchema,
    },
  );

  return {
    guestToken: response.guestToken,
    favorites: response.favorites,
    consentPersonalization: response.consent?.personalization,
  };
}

const RecommendationScoreResponseSchema = z.object({
  ok: z.boolean(),
  recommendations: z.object({
    itemIds: z.array(z.string()),
    breakdown: z.record(
      z.object({
        score: z.number(),
        reasons: z.array(z.string()),
      }),
    ),
    source: z.enum(['personalized', 'history', 'popular', 'signals']),
    consentPersonalization: z.boolean(),
    tasteSnapshot: z
      .object({
        tags: z.array(z.string()),
        cuisines: z.array(z.string()),
        heat: z.string().nullable(),
        tagScores: z.record(z.number()),
        cuisineAffinity: z.record(z.number()),
      })
      .nullable(),
    modelVersion: z.string().optional(),
    guestProfileId: z.string().optional().nullable(),
    holdout: z.boolean().optional(),
  }),
});

const AttachRecommendationSchema = z.object({
  itemId: z.string(),
  probability: z.number(),
  score: z.number(),
  matches: z.array(
    z.object({
      mainItemId: z.string(),
      probability: z.number(),
    }),
  ),
  jointOrderCount: z.number(),
  explorationApplied: z.boolean(),
});

const AttachRecommendationResponseSchema = z.object({
  ok: z.boolean(),
  surface: z.string(),
  modelVersion: z.string(),
  recommendations: z.array(AttachRecommendationSchema),
});

export interface RecommendationScoreItemInput {
  id: string;
  tags?: string[];
  cuisines?: string[];
  tasteHeat?: string | null;
  popular?: boolean;
  categoryId?: string | null;
  marginCents?: number | null;
  prepMinutes?: number | null;
  station?: string | null;
  isNew?: boolean;
}

export interface RecommendationScoreResult {
  itemIds: string[];
  breakdown: Record<string, RecommendationBreakdown>;
  source: RecommendationSource;
  consentPersonalization: boolean;
  tasteSnapshot: RecommendationTasteSnapshot | null;
  modelVersion?: string;
  guestProfileId?: string | null;
  holdout?: boolean;
}

export interface AttachRecommendationMatch {
  mainItemId: string;
  probability: number;
}

export interface AttachRecommendation {
  itemId: string;
  probability: number;
  score: number;
  matches: AttachRecommendationMatch[];
  jointOrderCount: number;
  explorationApplied: boolean;
}

export interface AttachRecommendationResponse {
  surface: string;
  modelVersion: string;
  recommendations: AttachRecommendation[];
}

export async function fetchRecommendationScores(params: {
  tenantSlug: string;
  guestToken?: string;
  locationId?: string | null;
  items: RecommendationScoreItemInput[];
  limit?: number;
  signal?: AbortSignal;
}): Promise<{ recommendations: RecommendationScoreResult } | null> {
  const { tenantSlug, signal, ...body } = params;
  if (!body.items.length) {
    return null;
  }
  const response = await jsonRequest<z.infer<typeof RecommendationScoreResponseSchema>>(
    '/public/recommendations/score',
    {
      method: 'POST',
      headers: buildHeaders(tenantSlug, true),
      body: {
        ...body,
        locationId: body.locationId ?? undefined,
      },
      signal,
      schema: RecommendationScoreResponseSchema,
    },
  );

  return response;
}

export async function fetchAttachRecommendations(params: {
  tenantSlug: string;
  mainItemIds: string[];
  limit?: number;
  signal?: AbortSignal;
}): Promise<AttachRecommendationResponse | null> {
  const { tenantSlug, signal, mainItemIds, limit } = params;
  const uniqueMainItemIds = Array.from(new Set(mainItemIds.map((id) => id.trim()).filter((id) => id.length > 0)));
  if (uniqueMainItemIds.length === 0) {
    return null;
  }
  const response = await jsonRequest<z.infer<typeof AttachRecommendationResponseSchema>>(
    '/public/recommendations/attach',
    {
      method: 'POST',
      headers: buildHeaders(tenantSlug, true),
      body: {
        mainItemIds: uniqueMainItemIds,
        limit,
      },
      signal,
      schema: AttachRecommendationResponseSchema,
    },
  );

  return {
    surface: response.surface,
    modelVersion: response.modelVersion,
    recommendations: response.recommendations,
  };
}

export interface PublicRecommendationSignal {
  menuItemId: string;
  addItemCount: number;
  openCustomizationCount: number;
  dismissedCount: number;
  totalActions: number;
  score: number;
  lastActionAt: string | null;
}

export async function getRecommendationSignals(params: {
  tenantSlug: string;
  locationId?: string | null;
  hours?: number;
  limit?: number;
  signal?: AbortSignal;
}): Promise<PublicRecommendationSignal[]> {
  const query = new URLSearchParams();
  if (params.locationId) {
    query.set('locationId', params.locationId);
  }
  if (typeof params.hours === 'number') {
    query.set('hours', String(params.hours));
  }
  if (typeof params.limit === 'number') {
    query.set('limit', String(params.limit));
  }
  const queryString = query.toString();
  const response = await jsonRequest<{ ok: true; signals: PublicRecommendationSignal[] }>(
    `/public/recommendations/signals${queryString ? `?${queryString}` : ''}`,
    {
      method: 'GET',
      headers: buildHeaders(params.tenantSlug, true),
      signal: params.signal,
    },
  );
  return response.signals;
}

export interface RecommendationMetric {
  date: string | null;
  impressions: number;
  actionCount: number;
  orderActionCount: number;
  uniqueProfiles: number;
  avgTasteProfileAgeHours: number;
  ctr: number;
  diversityAtN: number;
  noveltyPct: number;
  recOrderAov: number;
  allOrderAov: number;
  aovDelta: number;
  profitPer1kImpressions: number;
  attachRate: number;
  attachIncrementalAov: number;
}

export async function getRecommendationMetrics(params: {
  tenantSlug: string;
  days?: number;
  signal?: AbortSignal;
}): Promise<RecommendationMetric[]> {
  const query = new URLSearchParams();
  if (typeof params.days === 'number') {
    query.set('days', String(params.days));
  }
  const response = await jsonRequest<{ ok: true; metrics: RecommendationMetric[] }>(
    `/public/recommendations/metrics${query.size > 0 ? `?${query.toString()}` : ''}`,
    {
      method: 'GET',
      headers: buildHeaders(params.tenantSlug, true),
      signal: params.signal,
    },
  );
  return response.metrics;
}

export interface MenuItemReviewList {
  reviews: MenuItemReviewEntry[];
  nextCursor: string | null;
}

export interface SubmitMenuReviewResult {
  review: MenuItemReviewEntry;
  invite: MenuReviewInvite;
  stats: { menuItemId: string; averageRating: number | null; ratingsCount: number } | null;
}

export async function getMenuReviewInvite(params: {
  tenantSlug: string;
  inviteToken: string;
  signal?: AbortSignal;
}): Promise<MenuReviewInvite> {
  const query = new URLSearchParams({ tenant: params.tenantSlug });
  const response = await jsonRequest<{ ok: true; invite: MenuReviewInvite }>(
    `/public/menu/review-invites/${encodeURIComponent(params.inviteToken)}?${query.toString()}`,
    {
      method: 'GET',
      signal: params.signal,
      schema: MenuReviewInviteResponseSchema,
    },
  );
  return response.invite;
}

export async function getMenuItemReviews(params: {
  tenantSlug: string;
  menuItemId: string;
  limit?: number;
  cursor?: string | null;
  signal?: AbortSignal;
}): Promise<MenuItemReviewList> {
  const query = new URLSearchParams({ tenant: params.tenantSlug });
  if (params.limit && Number.isFinite(params.limit)) {
    query.set('limit', String(params.limit));
  }
  if (params.cursor) {
    query.set('cursor', params.cursor);
  }
  return jsonRequest<MenuItemReviewList>(
    `/public/menu/items/${encodeURIComponent(params.menuItemId)}/reviews?${query.toString()}`,
    {
      method: 'GET',
      signal: params.signal,
      schema: MenuItemReviewListSchema,
    },
  );
}

export async function submitMenuReview(params: {
  tenantSlug: string;
  inviteToken: string;
  menuItemId: string;
  rating: number;
  comment?: string | null;
  locale?: string | null;
  signal?: AbortSignal;
}): Promise<SubmitMenuReviewResult> {
  const response = await jsonRequest<SubmitMenuReviewResult>('/public/menu/reviews', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: {
      tenantSlug: params.tenantSlug,
      inviteToken: params.inviteToken,
      menuItemId: params.menuItemId,
      rating: params.rating,
      comment: params.comment ?? undefined,
      locale: params.locale ?? undefined,
    },
    signal: params.signal,
    schema: SubmitMenuReviewResponseSchema,
  });
  return response;
}
